import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { applyTheme, applyFavicon, type ThemeTokens } from "@/lib/theme";

function getPreviewId(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("themePreview");
}

export function ThemeApplier() {
  const [previewId, setPreviewId] = useState<string | null>(() => getPreviewId());

  useEffect(() => {
    const update = () => setPreviewId(getPreviewId());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const { data } = useQuery({
    queryKey: ["active-theme-and-favicon", previewId],
    queryFn: async () => {
      const themeQuery = previewId
        ? supabase.from("ui_themes").select("tokens").eq("id", previewId).maybeSingle()
        : supabase.from("ui_themes").select("tokens").eq("is_active", true).maybeSingle();
      const [{ data: theme }, { data: settings }] = await Promise.all([
        themeQuery,
        supabase.from("site_settings").select("favicon_url").eq("id", 1).maybeSingle(),
      ]);
      return {
        tokens: (theme?.tokens ?? {}) as ThemeTokens,
        faviconUrl: (settings as { favicon_url?: string | null } | null)?.favicon_url ?? null,
      };
    },
    staleTime: previewId ? 0 : 5 * 60_000,
  });

  useEffect(() => {
    if (!data) return;
    applyTheme(data.tokens);
    applyFavicon(data.faviconUrl);
  }, [data]);

  return null;
}
